import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CartLine, Totals, formatCents, isMissingCost } from '../money';

/** Guard window after the dialog opens during which the confirm button
 *  stays disabled. A double-tap on PURCHASE would otherwise open this
 *  and dismiss it in one gesture, which defeats the whole point. */
const ARM_DELAY_MS = 800;

type Props = {
  open: boolean;
  lines: CartLine[];
  employeeTotals: Totals;
  retailTotals: Totals;
  onCancel: () => void;
  onConfirm: () => void;
};

/** The deliberately loud one. Full-bleed red, unequal buttons, cancel
 *  is the default action, and the backdrop does nothing. */
const EmployeeConfirmDialog: React.FC<Props> = ({
  open,
  lines,
  employeeTotals,
  retailTotals,
  onCancel,
  onConfirm,
}) => {
  const [armed, setArmed] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setArmed(false);
      return;
    }
    const t = setTimeout(() => setArmed(true), ARM_DELAY_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Cancel owns the focus, so a stray Enter backs out rather than
  // committing a discounted sale.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  const giveUp = retailTotals.total - employeeTotals.total;
  const missing = lines.filter((l) => isMissingCost(l, 'employee'));

  return (
    <Dialog
      open={open}
      // Escape cancels; clicking the backdrop does nothing. A confirm
      // this consequential shouldn't be dismissible by a stray tap.
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') onCancel();
      }}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(183, 28, 28, 0.96)',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        },
      }}
    >
      <Box sx={{ maxWidth: 720, width: '100%', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <WarningAmberIcon key={i} sx={{ fontSize: 44 }} />
          ))}
        </Box>

        <Typography
          sx={{ fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.1, mb: 2 }}
        >
          EMPLOYEE DISCOUNT IS ACTIVE
        </Typography>

        <Typography sx={{ fontSize: '1.25rem', mb: 3 }}>
          This sale is priced at <strong>COST</strong>, not retail.
        </Typography>

        <Box
          sx={{
            backgroundColor: 'rgba(0,0,0,0.22)',
            borderRadius: 2,
            px: 3,
            py: 2,
            mb: 3,
            textAlign: 'left',
            maxWidth: 460,
            mx: 'auto',
          }}
        >
          <AmountRow label="Retail value" value={retailTotals.total} />
          <AmountRow label="Charging" value={employeeTotals.total} />
          <AmountRow label="Store gives up" value={giveUp} emphasise />
        </Box>

        {missing.length > 0 && (
          <Box
            sx={{
              backgroundColor: 'rgba(0,0,0,0.22)',
              borderRadius: 2,
              px: 3,
              py: 2,
              mb: 3,
              textAlign: 'left',
              maxWidth: 460,
              mx: 'auto',
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              No cost on file — charged at retail:
            </Typography>
            {missing.map((l) => (
              <Typography key={l.hit.id} variant="body2">
                • {l.hit.name}
              </Typography>
            ))}
          </Box>
        )}

        <Typography sx={{ fontSize: '1.1rem', mb: 3 }}>
          Only proceed if this is a staff purchase.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          {/* Cancel is bigger, first, and focused. Confirm is the
              smaller, harder-to-hit option on purpose. */}
          <Button
            ref={cancelRef}
            onClick={onCancel}
            variant="contained"
            sx={{
              flex: '1 1 300px',
              maxWidth: 360,
              py: 3,
              fontSize: '1.4rem',
              fontWeight: 800,
              backgroundColor: '#ffffff',
              color: '#b71c1c',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            NO — GO BACK
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!armed}
            variant="outlined"
            sx={{
              flex: '0 1 240px',
              py: 2,
              fontSize: '1rem',
              fontWeight: 700,
              color: '#ffffff',
              borderColor: 'rgba(255,255,255,0.7)',
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(0,0,0,0.15)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.45)',
                borderColor: 'rgba(255,255,255,0.25)',
              },
            }}
          >
            {armed ? 'YES, EMPLOYEE PURCHASE' : 'WAIT…'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const AmountRow: React.FC<{
  label: string;
  value: number;
  emphasise?: boolean;
}> = ({ label, value, emphasise }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      py: 0.5,
    }}
  >
    <Typography sx={{ fontWeight: emphasise ? 800 : 500 }}>{label}</Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: emphasise ? '1.6rem' : '1.25rem',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {formatCents(value)}
    </Typography>
  </Box>
);

export default EmployeeConfirmDialog;
